
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Copy, RefreshCw, ThumbsUp } from 'lucide-react';
import { GeneratedContent } from './types';
import { getPlatformIcon } from './utils';

interface GeneratedContentListProps {
  contentItems: GeneratedContent[];
  onCopy: (content: string) => void;
  onRegenerate?: (contentId: string) => void;
}

const GeneratedContentList: React.FC<GeneratedContentListProps> = ({ 
  contentItems, 
  onCopy,
  onRegenerate 
}) => {
  return (
    <Card className="bg-background/70">
      <CardHeader className="pb-2">
        <CardTitle>Generated Content</CardTitle>
        <CardDescription>Your AI-generated social media content</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            {contentItems.map((content) => (
              <Card key={content.id} className="overflow-hidden">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="flex items-center gap-1 bg-background/50">
                        {getPlatformIcon(content.platform)}
                        <span className="capitalize">{content.type}</span>
                      </Badge>
                      
                      <Badge 
                        variant="outline" 
                        className="flex items-center gap-1 bg-green-500/10 text-green-500 border-green-500/30"
                      >
                        <ThumbsUp className="h-3 w-3" />
                        <span>{content.engagement}% Engagement</span>
                      </Badge>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onCopy(content.content)}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" />
                      Copy
                    </Button>
                  </div>
                  
                  <div className="bg-muted/30 p-4 rounded-md whitespace-pre-line">
                    {content.content}
                  </div>
                  
                  <div className="flex justify-end mt-3">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onRegenerate && onRegenerate(content.id)}
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1" />
                      Regenerate
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default GeneratedContentList;
